// CONTRACT:I8-LOCAL-SETTINGS.1.0
/** The persisted COP display-settings store.
 *
 *  Contract (CONTRACT:I8-LOCAL-SETTINGS.1.0):
 *   - getSettings(): Settings        — the EFFECTIVE (merged) snapshot.
 *   - setSetting(path, value)        — TYPED write to the active scope; unknown key or wrong-typed
 *                                       value throws (fail closed), persists + notifies on success.
 *   - subscribe(cb)                  — observe effective-settings changes; returns an unsubscribe.
 *   - setPersona(id | null)          — switch the active scope (per-persona with device fallback).
 *
 *  PERSISTENCE MODEL — one JSON blob, two scope kinds:
 *    { version, device: {path→value}, personas: { <id>: {path→value} } }
 *  EFFECTIVE = DEFAULT_SETTINGS  ⊕  device-overrides  ⊕  active-persona-overrides   (later wins).
 *  WRITES land in the ACTIVE scope: the active persona if one is set, else the device scope.
 *  A persona with no stored overrides therefore FALLS BACK to the device scope (then defaults) —
 *  the "per-persona with a device fallback" rule (safer for classification-sensitive display).
 *
 *  This store is SEPARATE from CONTRACT:I6-LOCAL-PERSIST (the envelope IndexedDB store) — settings
 *  live in their own keyspace and never touch the I6 envelope-merge. Display preferences ONLY:
 *  nothing here can change WHAT is visible (the projection / PDP outcome), only HOW it draws. */

import {
  defaultSettings,
  isSettingPath,
  SETTING_PATHS,
  SETTING_PATH_KEYS,
  SETTINGS_SCHEMA_VERSION,
} from "./types.ts";
import type {
  LayoutRecipe,
  MarkerMode,
  Settings,
  SettingPath,
  SettingValueByPath,
} from "./types.ts";
import { ErrInvalidSettingValue, ErrUnknownSettingKey } from "./errors.ts";

/** An unsubscribe handle returned by subscribe(). */
export type Unsub = () => void;

/** The minimal synchronous key→value storage the store persists through (localStorage-shaped).
 *  Injectable so the store is testable in node (no DOM) and can target a non-localStorage backend. */
export interface StorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Flat per-scope overrides: a partial path→value map. */
type ScopeOverrides = Partial<Record<SettingPath, unknown>>;

/** The persisted on-disk shape. */
interface PersistedState {
  version: number;
  device: ScopeOverrides;
  personas: Record<string, ScopeOverrides>;
}

export interface SettingsStoreOptions {
  /** Storage backend. Defaults to localStorage when present, else an in-memory adapter. */
  storage?: StorageAdapter;
  /** Storage key the JSON blob lives under. Default "tak-tdf:settings". */
  storageKey?: string;
  /** Initial active persona id (the logged-in identity). null/undefined ⇒ device scope. */
  persona?: string | null;
}

const DEFAULT_STORAGE_KEY = "tak-tdf:settings";

/** An in-memory StorageAdapter (the fallback when no localStorage; also handy in tests). */
export function memoryStorage(): StorageAdapter {
  const map = new Map<string, string>();
  return {
    getItem: (k) => (map.has(k) ? (map.get(k) as string) : null),
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
  };
}

/** Resolve the default storage: real localStorage if reachable, else in-memory (never throws). */
function defaultStorage(): StorageAdapter {
  try {
    const ls = (globalThis as { localStorage?: StorageAdapter }).localStorage;
    if (ls && typeof ls.getItem === "function" && typeof ls.setItem === "function") {
      return ls;
    }
  } catch {
    // Accessing localStorage can throw (privacy mode / sandbox) — fall through to memory.
  }
  return memoryStorage();
}

/** Set one validated leaf value into a Settings object. Exhaustive over SettingPath (a new path
 *  without a case here is a compile error — the switch is the type-checked write surface). */
function setLeaf(s: Settings, path: SettingPath, value: unknown): void {
  switch (path) {
    case "renderProfile":
      s.renderProfile = value as string;
      return;
    case "markerMode":
      s.markerMode = value as MarkerMode;
      return;
    case "layout":
      s.layout = value as LayoutRecipe;
      return;
    case "reducedMotion":
      s.reducedMotion = value as boolean;
      return;
    case "iconDensityLod":
      s.iconDensityLod = value as boolean;
      return;
    case "blueForceAlwaysProminent":
      s.blueForceAlwaysProminent = value as boolean;
      return;
    case "fade.neutral":
      s.fade.neutral = value as number;
      return;
    case "fade.unknown":
      s.fade.unknown = value as number;
      return;
    case "advanced.vectorScale":
      s.advanced.vectorScale = value as number;
      return;
    case "advanced.ellipse.shipSigma":
      s.advanced.ellipse.shipSigma = value as number;
      return;
    case "advanced.ellipse.airSigma":
      s.advanced.ellipse.airSigma = value as number;
      return;
    case "advanced.ellipse.unknownAirSigma":
      s.advanced.ellipse.unknownAirSigma = value as number;
      return;
    case "advanced.dropLine.minZoom":
      s.advanced.dropLine.minZoom = value as number;
      return;
    case "advanced.dropLine.minAltitudeM":
      s.advanced.dropLine.minAltitudeM = value as number;
      return;
    case "advanced.dropLine.groundDotOpacity":
      s.advanced.dropLine.groundDotOpacity = value as number;
      return;
    case "advanced.dropLine.lineOpacity":
      s.advanced.dropLine.lineOpacity = value as number;
      return;
    // Array leaves are COPIED on apply so the effective snapshot never aliases the stored
    // array (a consumer mutating getSettings().alerts.* cannot poison store state).
    case "alerts.readIds":
      s.alerts.readIds = [...(value as string[])];
      return;
    case "alerts.clearedIds":
      s.alerts.clearedIds = [...(value as string[])];
      return;
    case "user.callsign":
      s.user.callsign = value as string;
      return;
    case "user.useRealName":
      s.user.useRealName = value as boolean;
      return;
    case "presence.shareLocation":
      s.presence.shareLocation = value as boolean;
      return;
    case "presence.updateSeconds":
      s.presence.updateSeconds = value as number;
      return;
  }
}

/** Sanitise an untrusted overrides map: keep ONLY known leaf paths whose value passes its
 *  validator; drop everything else (a corrupt/foreign/old blob can never poison the shape).
 *  An IDENTITY-SCOPED leaf found in the DEVICE scope is dropped too — a hand-edited/foreign
 *  blob must not bleed one user's per-identity state into every user via the device merge. */
function sanitizeOverrides(raw: unknown, scopeKind: "device" | "persona"): ScopeOverrides {
  const out: ScopeOverrides = {};
  if (!raw || typeof raw !== "object") return out;
  for (const key of SETTING_PATH_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(raw, key)) continue;
    if (scopeKind === "device" && SETTING_PATHS[key].identityScoped) continue;
    const v = (raw as Record<string, unknown>)[key];
    if (SETTING_PATHS[key].accepts(v)) out[key] = v;
  }
  return out;
}

/** Parse + sanitise a persisted blob. Returns a fresh empty state on any parse/shape failure. */
function parsePersisted(json: string | null): PersistedState {
  const empty: PersistedState = { version: SETTINGS_SCHEMA_VERSION, device: {}, personas: {} };
  if (!json) return empty;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return empty;
  }
  if (!parsed || typeof parsed !== "object") return empty;
  const obj = parsed as Record<string, unknown>;
  const device = sanitizeOverrides(obj.device, "device");
  const personas: Record<string, ScopeOverrides> = {};
  if (obj.personas && typeof obj.personas === "object") {
    for (const [id, ov] of Object.entries(obj.personas as Record<string, unknown>)) {
      personas[id] = sanitizeOverrides(ov, "persona");
    }
  }
  return { version: SETTINGS_SCHEMA_VERSION, device, personas };
}

export class SettingsStore {
  private readonly storage: StorageAdapter;
  private readonly storageKey: string;
  private state: PersistedState;
  private activePersona: string | null;
  private readonly subscribers = new Set<(s: Settings) => void>();

  constructor(opts: SettingsStoreOptions = {}) {
    this.storage = opts.storage ?? defaultStorage();
    this.storageKey = opts.storageKey ?? DEFAULT_STORAGE_KEY;
    this.activePersona = opts.persona ?? null;
    this.state = parsePersisted(this.safeRead());
  }

  // --- public contract ---------------------------------------------------------------------

  /** The EFFECTIVE (merged) settings: DEFAULTS ⊕ device ⊕ active-persona. A fresh object each
   *  call so a consumer can't mutate the store's internal state. */
  getSettings(): Settings {
    const s = defaultSettings();
    this.applyScope(s, this.state.device);
    if (this.activePersona) {
      const personaOv = this.state.personas[this.activePersona];
      if (personaOv) this.applyScope(s, personaOv);
    }
    return s;
  }

  /** TYPED write to the ACTIVE scope. Throws ErrUnknownSettingKey for an unknown key and
   *  ErrInvalidSettingValue for a wrong-typed/out-of-range value (the previous value is kept,
   *  nothing persists). On success: persists + notifies subscribers with the new effective snapshot.
   *  IDENTITY-SCOPED leaves (PathSpec.identityScoped) are DROPPED — no write, no persist, no
   *  notify — when no persona is active: writing them into the device scope would bleed one
   *  user's per-identity state into every user via the device-fallback merge (fail closed). */
  setSetting<K extends SettingPath>(key: K, value: SettingValueByPath[K]): void;
  setSetting(key: string, value: unknown): void {
    if (!isSettingPath(key)) {
      throw new ErrUnknownSettingKey(key);
    }
    if (!SETTING_PATHS[key].accepts(value)) {
      throw new ErrInvalidSettingValue(key, value);
    }
    if (SETTING_PATHS[key].identityScoped && !this.activePersona) return;
    const scope = this.activeScope();
    // Defensive copy for array leaves: the caller keeps its reference, so a later caller-side
    // mutation can never silently rewrite the stored (and next-persisted) value.
    scope[key] = Array.isArray(value) ? [...value] : value;
    this.persist();
    this.notify();
  }

  /** Subscribe to effective-settings changes. Returns an unsubscribe handle. */
  subscribe(cb: (s: Settings) => void): Unsub {
    this.subscribers.add(cb);
    return () => {
      this.subscribers.delete(cb);
    };
  }

  /** Switch the ACTIVE persona scope (e.g. on login / persona switch). null ⇒ device scope.
   *  The effective settings may change (a different persona's overrides), so subscribers are
   *  notified. No write occurs (no override changed). No-op (no notify) if unchanged. */
  setPersona(personaId: string | null): void {
    const next = personaId ?? null;
    if (next === this.activePersona) return;
    this.activePersona = next;
    this.notify();
  }

  /** The currently active persona id (null ⇒ device scope). */
  getActivePersona(): string | null {
    return this.activePersona;
  }

  /** Clear overrides. scope="active" clears the active scope (persona if set, else device);
   *  "device" clears the device scope; "all" clears everything. Persists + notifies. */
  reset(scope: "active" | "device" | "all" = "active"): void {
    if (scope === "all") {
      this.state = { version: SETTINGS_SCHEMA_VERSION, device: {}, personas: {} };
    } else if (scope === "device") {
      this.state.device = {};
    } else if (this.activePersona) {
      this.state.personas[this.activePersona] = {};
    } else {
      this.state.device = {};
    }
    this.persist();
    this.notify();
  }

  // --- internals ---------------------------------------------------------------------------

  /** The scope writes land in: the active persona's overrides (created on demand) or device. */
  private activeScope(): ScopeOverrides {
    if (this.activePersona) {
      let ov = this.state.personas[this.activePersona];
      if (!ov) {
        ov = {};
        this.state.personas[this.activePersona] = ov;
      }
      return ov;
    }
    return this.state.device;
  }

  /** Apply a scope's overrides onto a Settings draft (only validated leaves are present). */
  private applyScope(s: Settings, overrides: ScopeOverrides): void {
    for (const key of SETTING_PATH_KEYS) {
      if (!Object.prototype.hasOwnProperty.call(overrides, key)) continue;
      setLeaf(s, key, overrides[key]);
    }
  }

  private safeRead(): string | null {
    try {
      return this.storage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private persist(): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // A full/sandboxed storage must not crash the app — settings stay live in memory.
    }
  }

  private notify(): void {
    const snapshot = this.getSettings();
    for (const cb of this.subscribers) cb(snapshot);
  }
}

// --- the shared singleton (one instance for the whole app) ---------------------------------

let _shared: SettingsStore | null = null;

/** The process-wide settings store singleton — the ONE instance the SettingsDrawer (writes) and
 *  Bundle A's render backend (reads) share. Lazily created against the default storage. */
export function getSettingsStore(): SettingsStore {
  if (!_shared) _shared = new SettingsStore();
  return _shared;
}

/** Convenience read of the effective settings off the singleton — Bundle A's primary entry. */
export function getSettings(): Settings {
  return getSettingsStore().getSettings();
}

/** TEST-ONLY: replace/clear the singleton so a test can inject a fresh store. Not for app code. */
export function __setSharedStoreForTest(store: SettingsStore | null): void {
  _shared = store;
}
